import React, { useCallback, useEffect, useState } from 'react';
import {
  deleteReportedPost,
  dismissReport,
  getReportsApiPayload,
  isReportsApiSuccess,
  listReportedPosts,
} from '../../services/reportsApi';
import {
  formatDateTime,
  PAGE_SIZE,
  STATUS_FILTERS,
  useReportSession,
} from './reportHelpers';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

function ReportPosts() {
  const { showSnackbar, handleSessionExpired, snackbarEl } = useReportSession();

  const [status, setStatus] = useState('pending');
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [offset, setOffset] = useState(0);
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [reason, setReason] = useState('');

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listReportedPosts({
        status,
        q: query || undefined,
        limit: PAGE_SIZE,
        offset,
      });

      if (handleSessionExpired(res)) return;
      if (!isReportsApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load posts', 'error');
        setPosts([]);
        setTotal(0);
        setHasMore(false);
        return;
      }

      const payload = getReportsApiPayload(res) || {};
      setPosts(payload.posts || []);
      setTotal(payload.total ?? 0);
      setHasMore(Boolean(payload.pagination?.hasMore));
    } finally {
      setLoading(false);
    }
  }, [status, query, offset, handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const runSearch = () => {
    setOffset(0);
    setQuery(searchInput.trim());
  };

  const openDelete = (post) => {
    setReason('');
    setConfirm({ type: 'delete', post });
  };

  const openDismiss = (post) => {
    setReason('');
    setConfirm({ type: 'dismiss', post });
  };

  const markPostDeleted = (feedId) => {
    setPosts((prev) =>
      prev.map((p) => {
        if (Number(p.feedId) !== Number(feedId)) return p;
        return {
          ...p,
          moderationStatus: 'actioned',
          feed: {
            ...(p.feed || {}),
            isDeleted: true,
            deletedAt: new Date().toISOString(),
          },
        };
      })
    );
  };

  const markReportDismissed = (reportId) => {
    setPosts((prev) =>
      prev.map((p) =>
        Number(p.reportId) === Number(reportId)
          ? { ...p, moderationStatus: 'dismissed' }
          : p
      )
    );
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setActionLoading(true);
    try {
      let res;
      if (confirm.type === 'delete') {
        res = await deleteReportedPost(confirm.post.feedId, {
          reportId: confirm.post.reportId,
          reason: reason.trim() || undefined,
        });
      } else {
        res = await dismissReport(confirm.post.reportId, {
          reason: reason.trim() || undefined,
        });
      }

      if (handleSessionExpired(res)) return;
      if (!isReportsApiSuccess(res)) {
        showSnackbar(res?.message || 'Action failed', 'error');
        return;
      }

      if (confirm.type === 'delete') {
        markPostDeleted(confirm.post.feedId);
        const payload = getReportsApiPayload(res);
        if (payload?.alreadyDeleted) {
          showSnackbar('Post already removed · reports marked actioned');
        } else {
          showSnackbar(
            payload?.authorNotified
              ? 'Post deleted for everyone · author notified'
              : 'Post deleted for everyone'
          );
        }
      } else {
        markReportDismissed(confirm.post.reportId);
        showSnackbar('Report dismissed');
      }
      setConfirm(null);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmTitle =
    confirm?.type === 'delete' ? 'Delete post' : 'Dismiss report';
  const confirmCopy =
    confirm?.type === 'delete'
      ? 'Delete this post for everyone? It will be hidden from all feeds and marked as moderated. The author will be notified with the reason below.'
      : 'Dismiss this report without deleting the post?';
  const isDelete = confirm?.type === 'delete';

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder="Search feed id, report id, content, username…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch();
          }}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={runSearch}>
          Search
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {STATUS_FILTERS.map((f) => (
            <Chip
              key={f.value}
              label={f.label}
              clickable
              color={status === f.value ? 'primary' : 'default'}
              variant={status === f.value ? 'filled' : 'outlined'}
              onClick={() => {
                setStatus(f.value);
                setOffset(0);
              }}
            />
          ))}
        </Stack>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Preview</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Reporter / Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reported</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : posts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    No reported posts for this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              posts.map((post) => {
                const feed = post.feed || {};
                const alreadyDeleted = Boolean(feed.isDeleted || feed.deletedAt);
                const imageUrl = feed.imageUrl;
                return (
                  <TableRow key={`${post.reportId}-${post.feedId}`} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'grey.100',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {imageUrl ? (
                            <Box
                              component="img"
                              src={imageUrl}
                              alt=""
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {feed.isReel ? 'Reel' : 'Post'}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0, maxWidth: 220 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {feed.content || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Feed #{post.feedId}
                            {feed.isReel ? ' · Reel' : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        @{post.reportedUser?.username || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {post.reportedUserId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2">
                        @{post.reportingUser?.username || '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        title={post.reportDesc || ''}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {post.reportDesc || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip
                          size="small"
                          label={post.moderationStatus || '—'}
                          color={
                            post.moderationStatus === 'pending'
                              ? 'warning'
                              : post.moderationStatus === 'actioned'
                                ? 'success'
                                : 'default'
                          }
                        />
                        {alreadyDeleted && (
                          <Chip size="small" color="error" label="Already removed" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(feed.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Report #{post.reportId}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {post.moderationStatus === 'pending' && (
                          <Button size="small" onClick={() => openDismiss(post)}>
                            Dismiss
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={alreadyDeleted && post.moderationStatus !== 'pending'}
                          onClick={() => openDelete(post)}
                        >
                          {alreadyDeleted ? 'Mark actioned' : 'Delete post'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {total} total · showing {offset + 1}–
          {Math.min(offset + PAGE_SIZE, total || offset)}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            disabled={!hasMore || loading}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Next
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={Boolean(confirm)}
        onClose={() => !actionLoading && setConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{confirmCopy}</DialogContentText>
          <TextField
            fullWidth
            size="small"
            label={isDelete ? 'Reason (shown to author)' : 'Reason (optional)'}
            placeholder={
              isDelete
                ? 'e.g. Nudity or sexual content, Hate speech, Spam…'
                : undefined
            }
            helperText={
              isDelete
                ? 'Appears in the author\u2019s "post removed" notification, so keep it clear and user-friendly. Leave blank for a generic community-guidelines message.'
                : undefined
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={actionLoading} onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirm?.type === 'delete' ? 'error' : 'primary'}
            disabled={actionLoading}
            onClick={handleConfirm}
          >
            {actionLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : confirm?.type === 'delete' ? (
              'Delete'
            ) : (
              'Dismiss'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {snackbarEl}
    </Box>
  );
}

export default ReportPosts;
